// Client Portal — BuildIQ management page (the one-platform split: the
// CLIENT MANAGES here, the BUILDER WORKS in the BuildIQ app, CREW live in
// FieldIQ). Settings ride the BuildIQ service's /api/settings — the signed-in
// client admin IS the office persona, so no extra auth plumbing. "Open
// BuildIQ" hands the platform session across (#sso fragment, stripped on
// arrival) so the builder app opens signed-in.
import { useEffect, useState } from "react";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { BUILDIQ_URL, getToken } from "../lib/api";
import { BuildIQDocTemplates } from "./BuildIQDocTemplates";

const inputCls =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

// These are URLs the office PASTES INTO AN OUTSIDE SERVICE - a Twilio number,
// a phone calendar, a marketing form. They showed /.netlify/functions/* until
// 2026-09-10, which worked only because app/main.py keeps a deprecated alias
// alive. Canonical is /api/fn/* (hyveai-conventions SKILL.md).
//
// ⚠️ The alias must STAY regardless: facilities have already pasted the old
// URL into Twilio and subscribed phones to the old calendar feed. This fixes
// what NEW setups are told; it does not break the ones already configured.
const KEYS = [
  {
    id: "lead_key",
    field: (c) => c.lead_key,
    label: "Lead intake",
    path: "/api/fn/lead?k=",
  },
  {
    id: "ics_key",
    field: (c) => c.ics_key,
    label: "Calendar feed",
    path: "/api/fn/ics?k=",
  },
  {
    id: "twilio_webhook",
    field: (c) => c.twilio?.webhook_key,
    label: "Twilio inbound",
    path: "/api/fn/twilio?k=",
  },
  {
    id: "cc_webhook",
    field: (c) => c.companycam?.webhook_key,
    label: "CompanyCam webhook",
    path: "/api/fn/cc-webhook?k=",
  },
];

export const BuildIQPortal = ({ facility }) => {
  const fid = facility.facility_id;
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [twilioToken, setTwilioToken] = useState("");
  const [ccToken, setCcToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(null);
  const [msg, setMsg] = useState(null);

  const isAdmin = ["admin", "editor"].includes(
    facility.services?.find((s) => s.service_type === "construction")
      ?.access_role,
  );

  const api = async (path, options = {}) => {
    const res = await fetch(`${BUILDIQ_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok)
      throw new Error(data.detail || data.error || `HTTP ${res.status}`);
    return data;
  };

  const load = () =>
    api(`/api/settings?facility_id=${fid}`)
      .then((d) => {
        setCfg(d);
        setForm({
          company_name: d.company_name || "",
          license_line: d.license_line || "",
          footer_line: d.footer_line || "",
          default_salesman: d.default_salesman || "",
          logo_url: d.logo_url || "",
          autosend: d.autosend === true,
          twilio_sid: d.twilio?.sid || "",
          twilio_from: d.twilio?.from || "",
        });
      })
      .catch((e) => setMsg({ type: "err", text: e.message }));

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fid]);

  const openBuildIQ = async () => {
    // One-time 60s exchange code — the URL fragment never carries the JWT.
    try {
      const d = await api("/api/sso/mint", { method: "POST" });
      window.open(
        `${BUILDIQ_URL}/${fid}#sso=${encodeURIComponent(d.code)}`,
        "_blank",
        "noopener",
      );
    } catch {
      // fall back to a plain open; the user signs in normally
      window.open(`${BUILDIQ_URL}/${fid}`, "_blank", "noopener");
    }
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body = {
        facility_id: fid,
        company_name: form.company_name,
        license_line: form.license_line,
        footer_line: form.footer_line,
        default_salesman: form.default_salesman,
        logo_url: form.logo_url,
        autosend: form.autosend,
        twilio: { sid: form.twilio_sid, from: form.twilio_from },
      };
      if (twilioToken.trim()) body.twilio.token = twilioToken.trim();
      if (ccToken.trim()) body.companycam = { token: ccToken.trim() };
      const d = await api("/api/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setCfg(d);
      setTwilioToken("");
      setCcToken("");
      setMsg({ type: "ok", text: "Saved." });
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const rotate = async (id) => {
    if (
      !window.confirm(
        "Rotate this key? Anything using the old key (webhooks, calendar subscriptions, form integrations) stops working until updated.",
      )
    )
      return;
    setRotating(id);
    try {
      await api("/api/settings/rotate", {
        method: "POST",
        body: JSON.stringify({ facility_id: fid, key: id }),
      });
      await load();
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setRotating(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Launch card */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">BuildIQ</h2>
          <p className="text-sm text-slate-500">
            Bids, scheduling, billing and field packets — the builder workspace
            opens signed-in with your current session.
          </p>
        </div>
        <button
          onClick={openBuildIQ}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-slate-900 text-sm font-semibold rounded-lg hover:bg-amber-400"
        >
          <ExternalLink className="w-4 h-4" />
          Open BuildIQ
        </button>
      </div>

      {!isAdmin ? (
        <p className="text-sm text-slate-400 px-1">
          Settings are managed by your facility admin.
        </p>
      ) : !cfg ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
        </div>
      ) : (
        <>
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div>
            <h3 className="font-semibold text-slate-800">Company settings</h3>
            <p className="text-xs text-slate-400">
              Branding on your documents and sign-in page, reminder texting, and
              integration keys.
            </p>
          </div>
          {msg && (
            <p
              className={`text-sm ${msg.type === "ok" ? "text-green-600" : "text-red-600"}`}
            >
              {msg.text}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["company_name", "Company name"],
              ["default_salesman", "Default salesman"],
              ["license_line", "License line"],
              ["footer_line", "Footer line"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className={labelCls}>{label}</label>
                <input
                  className={inputCls}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className={labelCls}>Logo URL</label>
              <input
                className={inputCls}
                value={form.logo_url || ""}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://… — shown on the sign-in page and generated documents"
              />
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Texting (Twilio)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Account SID</label>
                <input
                  className={inputCls}
                  value={form.twilio_sid || ""}
                  onChange={(e) =>
                    setForm({ ...form, twilio_sid: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={labelCls}>From number</label>
                <input
                  className={inputCls}
                  value={form.twilio_from || ""}
                  onChange={(e) =>
                    setForm({ ...form, twilio_from: e.target.value })
                  }
                  placeholder="+14805551212"
                />
              </div>
              <div>
                <label className={labelCls}>Auth token</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  className={inputCls}
                  value={twilioToken}
                  onChange={(e) => setTwilioToken(e.target.value)}
                  placeholder={
                    cfg.twilio?.has_token
                      ? "Configured — blank keeps it"
                      : "Not set"
                  }
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={form.autosend === true}
                onChange={(e) =>
                  setForm({ ...form, autosend: e.target.checked })
                }
                className="rounded"
              />
              Send automated daily client reminders
            </label>
          </div>

          <div className="border border-slate-200 rounded-lg p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              CompanyCam
            </p>
            <label className={labelCls}>Access token</label>
            <input
              type="password"
              autoComplete="new-password"
              className={inputCls}
              value={ccToken}
              onChange={(e) => setCcToken(e.target.value)}
              placeholder={
                cfg.companycam?.has_token
                  ? "Configured — blank keeps it"
                  : "Not set"
              }
            />
          </div>

          <div className="border border-slate-200 rounded-lg p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Integration links
            </p>
            {KEYS.map((k) => {
              const value = k.field(cfg) || "";
              return (
                <div key={k.id} className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-600 w-40 shrink-0">
                    {k.label}
                  </span>
                  <input
                    readOnly
                    value={
                      value
                        ? `${BUILDIQ_URL}${k.path}${value}`
                        : "— not generated —"
                    }
                    onFocus={(e) => e.target.select()}
                    className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-mono text-slate-600 bg-slate-50"
                  />
                  <button
                    type="button"
                    onClick={() => rotate(k.id)}
                    disabled={rotating === k.id}
                    className="px-2.5 py-1.5 text-xs font-medium border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {rotating === k.id ? "…" : value ? "Rotate" : "Generate"}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="px-5 py-2 bg-amber-500 text-slate-900 text-sm font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Settings"}
            </button>
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>
        <BuildIQDocTemplates fid={fid} />
        </>
      )}
    </div>
  );
};
